import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Search, ArrowRight, BookOpen, Key, Shield, Gauge, Printer, Lock, Mail, Video, Download } from "lucide-react";

const ICONS = {
  "key-round": Key,
  shield: Shield,
  gauge: Gauge,
  printer: Printer,
  lock: Lock,
  mail: Mail,
  video: Video,
  download: Download,
};

export default function KnowledgeBasePage() {
  const [articles, setArticles] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/kb").then((r) => setArticles(r.data));
  }, []);

  const filtered = articles.filter(
    (a) =>
      !query ||
      a.title.toLowerCase().includes(query.toLowerCase()) ||
      a.summary.toLowerCase().includes(query.toLowerCase()) ||
      a.category.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="p-8 lg:p-12 max-w-6xl" data-testid="kb-page">
      <div className="mb-10 fade-up">
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-blue-600 mb-3">Self-serve</div>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl text-zinc-900">Knowledge Base</h1>
        <p className="text-zinc-500 mt-3 max-w-xl">Solutions to the most common IT issues. Step-by-step. No jargon.</p>
      </div>

      <div className="relative mb-10">
        <Search className="w-5 h-5 text-zinc-400 absolute left-5 top-1/2 -translate-y-1/2" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search articles..."
          className="w-full pl-14 pr-5 py-4 rounded-2xl border-2 border-zinc-200 bg-white text-base focus:border-blue-600 focus:outline-none transition"
          data-testid="kb-search"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-zinc-500" data-testid="kb-empty">No articles match your search.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((a) => {
            const Icon = ICONS[a.icon] || BookOpen;
            return (
              <Link
                key={a.id}
                to={`/app/kb/${a.id}`}
                className="bg-white p-6 rounded-2xl border border-zinc-200 hover:border-zinc-900 hover:shadow-xl hover:-translate-y-1 transition-all group"
                data-testid={`kb-card-${a.id}`}
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="w-11 h-11 bg-zinc-100 rounded-xl flex items-center justify-center group-hover:bg-blue-600 transition">
                    <Icon className="w-5 h-5 text-zinc-700 group-hover:text-white transition" strokeWidth={2} />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-zinc-400">{a.category}</span>
                </div>
                <h3 className="font-display font-bold text-lg text-zinc-900 mb-2 group-hover:text-blue-600 transition">{a.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed mb-4">{a.summary}</p>
                <div className="inline-flex items-center gap-1 text-sm font-semibold text-zinc-900 group-hover:gap-2 transition-all">
                  Read article <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
